import React from 'react';
import { Link } from 'react-router-dom';
import { Cookie, Settings, Eye, BarChart3, Shield } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import PublicLayout from '@/components/layout/PublicLayout';

export default function Cookies() {
  return (
    <PublicLayout>
      <SEOHead
        title="Politique des Cookies | Clairify"
        description="Politique de gestion des cookies de Clairify. Transparence totale sur les cookies utilisés, leur finalité, et comment les gérer."
        keywords={['cookies', 'politique cookies', 'gestion cookies', 'RGPD cookies']}
        canonicalUrl="https://clairify.fr/cookies"
        ogType="website"
      />
      <article className="legal-doc">
      <p className="home-kicker">Légal</p>
        {/* Title */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <Cookie className="w-10 h-10 text-purple-600 dark:text-purple-400" />
            <h1 className="text-4xl font-bold text-theme-primary">Politique de Cookies</h1>
          </div>
          <p className="text-lg text-theme-secondary">
            Information sur l'utilisation des cookies et technologies similaires sur Clairify
          </p>
          <p className="text-sm text-theme-tertiary mt-2">Dernière mise à jour : 13 octobre 2025</p>
        </div>

        <div className="space-y-12 text-theme-secondary leading-relaxed">
          {/* Qu'est-ce qu'un cookie */}
          <section>
            <h2 className="text-2xl font-semibold text-theme-primary mb-4 flex items-center gap-2">
              <Eye size={24} className="text-purple-600 dark:text-purple-400" />
              1. Qu'est-ce qu'un cookie ?
            </h2>
            <p className="mb-4">
              Un cookie est un petit fichier texte déposé sur votre terminal (ordinateur, smartphone, tablette) lors de la visite d'un site web. Il permet de stocker des informations relatives à votre navigation et de vous reconnaître lors de vos prochaines visites.
            </p>
            <div className="bg-theme-surface/50 p-6 rounded-xl border border-theme-border">
              <p className="font-semibold text-theme-primary mb-3">Les cookies peuvent servir à :</p>
              <ul className="list-disc list-inside space-y-2">
                <li>Mémoriser vos préférences (langue, affichage, etc.)</li>
                <li>Vous maintenir connecté à votre compte</li>
                <li>Analyser le trafic et l'utilisation du site</li>
                <li>Améliorer l'expérience utilisateur</li>
                <li>Sécuriser votre navigation</li>
              </ul>
            </div>
          </section>

          {/* Types de cookies */}
          <section>
            <h2 className="text-2xl font-semibold text-theme-primary mb-4">2. Types de cookies utilisés sur Clairify</h2>
            
            <div className="space-y-6">
              {/* Cookies essentiels */}
              <div className="bg-green-500/5 p-6 rounded-xl border border-green-500/20">
                <div className="flex items-start gap-3 mb-4">
                  <Shield className="text-green-400 flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="text-xl font-semibold text-theme-primary mb-2">2.1. Cookies strictement nécessaires</h3>
                    <p className="text-sm text-theme-secondary">Ces cookies sont indispensables au fonctionnement de la Plateforme. Vous ne pouvez pas les refuser.</p>
                  </div>
                </div>
                
                <table className="w-full text-sm mt-4">
                  <thead>
                    <tr className="border-b border-green-500/20">
                      <th className="text-left py-2 text-theme-primary">Cookie</th>
                      <th className="text-left py-2 text-theme-primary">Finalité</th>
                      <th className="text-center py-2 text-theme-primary">Durée</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-green-500/10">
                      <td className="py-3">supabase.auth.token</td>
                      <td className="py-3">Authentification et maintien de session</td>
                      <td className="text-center py-3">7 jours</td>
                    </tr>
                    <tr className="border-b border-green-500/10">
                      <td className="py-3">csrf_token</td>
                      <td className="py-3">Protection contre les attaques CSRF</td>
                      <td className="text-center py-3">Session</td>
                    </tr>
                    <tr className="border-b border-green-500/10">
                      <td className="py-3">cookie_consent</td>
                      <td className="py-3">Mémorisation de vos choix de cookies</td>
                      <td className="text-center py-3">13 mois</td>
                    </tr>
                    <tr>
                      <td className="py-3">language</td>
                      <td className="py-3">Mémorisation de la langue préférée</td>
                      <td className="text-center py-3">12 mois</td>
                    </tr>
                  </tbody>
                </table>
                <p className="text-xs text-theme-tertiary mt-4">
                  Base légale : Intérêt légitime (fourniture du service)
                </p>
              </div>

              {/* Cookies fonctionnels */}
              <div className="bg-orange-600/5 p-6 rounded-xl border border-orange-600/20">
                <div className="flex items-start gap-3 mb-4">
                  <Settings className="text-orange-600 flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="text-xl font-semibold text-theme-primary mb-2">2.2. Cookies fonctionnels</h3>
                    <p className="text-sm text-theme-secondary">Ces cookies améliorent votre expérience en mémorisant vos préférences.</p>
                  </div>
                </div>
                
                <table className="w-full text-sm mt-4">
                  <thead>
                    <tr className="border-b border-orange-600/20">
                      <th className="text-left py-2 text-theme-primary">Cookie</th>
                      <th className="text-left py-2 text-theme-primary">Finalité</th>
                      <th className="text-center py-2 text-theme-primary">Durée</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-orange-600/10">
                      <td className="py-3">theme_preference</td>
                      <td className="py-3">Mémorisation du thème (dark/light)</td>
                      <td className="text-center py-3">12 mois</td>
                    </tr>
                    <tr className="border-b border-orange-600/10">
                      <td className="py-3">sidebar_state</td>
                      <td className="py-3">État de la barre latérale (ouvert/fermé)</td>
                      <td className="text-center py-3">30 jours</td>
                    </tr>
                    <tr>
                      <td className="py-3">recent_sessions</td>
                      <td className="py-3">Sessions récentes (accès rapide)</td>
                      <td className="text-center py-3">7 jours</td>
                    </tr>
                  </tbody>
                </table>
                <p className="text-xs text-theme-tertiary mt-4">
                  Base légale : Consentement (peut être refusé)
                </p>
              </div>

              {/* Cookies analytiques */}
              <div className="bg-purple-600 dark:bg-purple-500/5 p-6 rounded-xl border border-purple-600 dark:border-purple-400/20">
                <div className="flex items-start gap-3 mb-4">
                  <BarChart3 className="text-purple-600 dark:text-purple-400 flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="text-xl font-semibold text-theme-primary mb-2">2.3. Cookies analytiques / de mesure d'audience</h3>
                    <p className="text-sm text-theme-secondary">Ces cookies nous aident à comprendre comment vous utilisez la Plateforme pour l'améliorer.</p>
                  </div>
                </div>
                
                <div className="bg-theme-surface/50 p-4 rounded-lg border border-theme-border mt-4">
                  <p className="font-semibold text-theme-primary mb-2">Outil utilisé : Google Analytics (version anonymisée)</p>
                  <table className="w-full text-sm mt-3">
                    <thead>
                      <tr className="border-b border-theme-border">
                        <th className="text-left py-2 text-theme-primary">Cookie</th>
                        <th className="text-left py-2 text-theme-primary">Finalité</th>
                        <th className="text-center py-2 text-theme-primary">Durée</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-theme-border">
                        <td className="py-3">_ga</td>
                        <td className="py-3">Différenciation des visiteurs</td>
                        <td className="text-center py-3">13 mois</td>
                      </tr>
                      <tr>
                        <td className="py-3">_ga_*</td>
                        <td className="py-3">Maintien de l'état de session</td>
                        <td className="text-center py-3">13 mois</td>
                      </tr>
                    </tbody>
                  </table>
                  <p className="text-xs text-theme-tertiary mt-4">
                    Données collectées : Pages visitées, durée de visite, provenance, type d'appareil (anonymisées, IP tronquée)
                  </p>
                </div>
                <p className="text-xs text-theme-tertiary mt-4">
                  Base légale : Consentement (peut être refusé)
                </p>
              </div>
            </div>
          </section>

          {/* Cookies tiers */}
          <section>
            <h2 className="text-2xl font-semibold text-theme-primary mb-4">3. Cookies de tiers</h2>
            <p className="mb-4">Certains cookies sont déposés par des services tiers que nous utilisons :</p>
            
            <div className="space-y-4">
              <div className="bg-theme-surface/50 p-5 rounded-xl border border-theme-border">
                <p className="font-semibold text-theme-primary mb-2">Stripe (Paiement)</p>
                <p className="text-sm text-theme-secondary mb-2">Cookies nécessaires au traitement sécurisé des paiements</p>
                <p className="text-xs text-theme-tertiary">Politique de confidentialité : <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-purple-600 dark:text-purple-400 hover:underline">https://stripe.com/privacy</a></p>
              </div>

              <div className="bg-theme-surface/50 p-5 rounded-xl border border-theme-border">
                <p className="font-semibold text-theme-primary mb-2">Supabase (Infrastructure)</p>
                <p className="text-sm text-theme-secondary mb-2">Cookies d'authentification et de session</p>
                <p className="text-xs text-theme-tertiary">Politique de confidentialité : <a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer" className="text-purple-600 dark:text-purple-400 hover:underline">https://supabase.com/privacy</a></p>
              </div>

              <div className="bg-theme-surface/50 p-5 rounded-xl border border-theme-border">
                <p className="font-semibold text-theme-primary mb-2">Google Analytics</p>
                <p className="text-sm text-theme-secondary mb-2">Cookies de mesure d'audience (anonymisés)</p>
                <p className="text-xs text-theme-tertiary">Politique de confidentialité : <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-purple-600 dark:text-purple-400 hover:underline">https://policies.google.com/privacy</a></p>
              </div>
            </div>
          </section>

          {/* Gestion des cookies */}
          <section>
            <h2 className="text-2xl font-semibold text-theme-primary mb-4">4. Comment gérer vos cookies ?</h2>
            
            <h3 className="text-xl font-semibold text-theme-primary mb-3">4.1. Via notre bandeau de consentement</h3>
            <p className="mb-4">
              Lors de votre première visite, un bandeau vous permet de choisir quels cookies vous acceptez. Vous pouvez modifier vos choix à tout moment en cliquant sur le bouton "Gérer les cookies" en bas de page.
            </p>

            <h3 className="text-xl font-semibold text-theme-primary mb-3 mt-6">4.2. Via votre navigateur</h3>
            <p className="mb-4">Vous pouvez configurer votre navigateur pour refuser tous les cookies ou être alerté lorsqu'un cookie est déposé :</p>
            
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { name: "Google Chrome", link: "https://support.google.com/chrome/answer/95647" },
                { name: "Mozilla Firefox", link: "https://support.mozilla.org/fr/kb/activer-desactiver-cookies" },
                { name: "Safari (macOS)", link: "https://support.apple.com/fr-fr/guide/safari/sfri11471/mac" },
                { name: "Microsoft Edge", link: "https://support.microsoft.com/fr-fr/microsoft-edge/supprimer-les-cookies-dans-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" }
              ].map((browser, idx) => (
                <div key={idx} className="bg-theme-surface/30 p-4 rounded-lg border border-theme-border/50">
                  <p className="font-semibold text-theme-primary mb-1">{browser.name}</p>
                  <a 
                    href={browser.link} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-sm text-purple-600 dark:text-purple-400 hover:underline"
                  >
                    Guide de configuration →
                  </a>
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              <p className="text-sm text-yellow-200">
                <strong>Attention :</strong> Le refus de certains cookies peut entraîner une dégradation de votre expérience ou rendre certaines fonctionnalités indisponibles.
              </p>
            </div>

            <h3 className="text-xl font-semibold text-theme-primary mb-3 mt-6">4.3. Opt-out Google Analytics</h3>
            <p>
              Vous pouvez installer le module complémentaire de navigateur pour la désactivation de Google Analytics : <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-purple-600 dark:text-purple-400 hover:underline">https://tools.google.com/dlpage/gaoptout</a>
            </p>
          </section>

          {/* Durée de conservation */}
          <section>
            <h2 className="text-2xl font-semibold text-theme-primary mb-4">5. Durée de conservation des cookies</h2>
            <p className="mb-4">Conformément aux recommandations de la CNIL :</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong className="text-theme-primary">Cookies de session :</strong> Supprimés automatiquement à la fermeture du navigateur</li>
              <li><strong className="text-theme-primary">Cookies fonctionnels :</strong> Conservés jusqu'à 13 mois maximum</li>
              <li><strong className="text-theme-primary">Cookies analytiques :</strong> Conservés jusqu'à 13 mois maximum</li>
              <li><strong className="text-theme-primary">Consentement :</strong> Votre choix est mémorisé pendant 13 mois</li>
            </ul>
            <p className="mt-4 text-sm text-theme-secondary">
              Au-delà de ces durées, votre consentement sera à nouveau demandé.
            </p>
          </section>

          {/* Mises à jour */}
          <section>
            <h2 className="text-2xl font-semibold text-theme-primary mb-4">6. Modifications de la politique</h2>
            <p>
              Nous nous réservons le droit de modifier cette Politique de Cookies à tout moment. La version en vigueur est toujours accessible sur cette page.
            </p>
            <p className="mt-4">
              En cas de modification substantielle (nouveaux cookies, nouvelles finalités), nous vous en informerons et solliciterons à nouveau votre consentement si nécessaire.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-gradient-to-br from-purple-500/10 to-indigo-600/5 p-8 rounded-2xl border border-purple-600 dark:border-purple-400/20">
            <h2 className="text-2xl font-semibold text-theme-primary mb-4">7. Contact</h2>
            <p className="mb-6">Pour toute question relative aux cookies :</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="font-semibold text-theme-primary mb-2">STELLARMEET</p>
                <p className="text-sm text-theme-secondary">25 rue de Ponthieu</p>
                <p className="text-sm text-theme-secondary">75008 Paris, France</p>
              </div>
              <div>
                <p className="font-semibold text-theme-primary mb-2">Email</p>
                <p className="text-sm">
                  <a href="mailto:privacy@stellarmeet.com" className="text-purple-600 dark:text-purple-400 hover:underline">privacy@stellarmeet.com</a>
                </p>
              </div>
            </div>
          </section>
        </div>

        {/* Footer links */}
        <div className="mt-12 pt-8 border-t border-theme-border flex flex-wrap gap-4 text-sm">
          <Link to="/legal" className="text-purple-600 dark:text-purple-400 hover:underline">Mentions Légales</Link>
          <Link to="/privacy" className="text-purple-600 dark:text-purple-400 hover:underline">Politique de Confidentialité</Link>
          <Link to="/terms" className="text-purple-600 dark:text-purple-400 hover:underline">Conditions Générales</Link>
        </div>
      </article>
 </PublicLayout>
 );
}
